import React from "react";
import {
  SwitchWrapper,
  SwitchTrack,
  SwitchThumb,
  SwitchText,
  SwitchLabel,
} from "./styles";

type Props = Readonly<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  "data-test-id"?: string;
}>;

/**
 * Pill-shaped toggle switch with ON/OFF labels.
 * - OFF: light grey track, dark grey thumb on left
 * - ON: light blue track, darker blue thumb on right
 * - Disabled: grey whether ON or OFF, the ON/OFF text still shows the state
 * - Error: red outline around the switch
 */
export const Switch = ({
  checked,
  onChange,
  disabled = false,
  error = false,
  label,
  "data-test-id": testId,
}: Props): JSX.Element => {
  const handleClick = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <SwitchWrapper>
      <SwitchTrack
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled}
        $checked={checked}
        $error={error}
        $disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        data-test-id={testId}
      >
        <SwitchThumb $checked={checked} $disabled={disabled} />
        <SwitchText $checked={checked} $disabled={disabled}>
          {checked ? "ON" : "OFF"}
        </SwitchText>
      </SwitchTrack>
      {label && <SwitchLabel $error={error}>{label}</SwitchLabel>}
    </SwitchWrapper>
  );
};
